import API from '../AxiosInstance';
import { executeChunkedRequests, BackendLimits } from '@/components/utils/request-chunking';

export default {
  getAllPods: function() {
    return API.get('pods');
  },

  /**
   *
   * @param {string} podID
   * @param {AbortController} [abortController]
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  getPodByID: function(podID, abortController) {
    return API.get('pods/'+podID, {
      signal: abortController?.signal ?? undefined,
    });
  },

  getPodMeta: function(podID) {
    return API.get('pods/' + podID + "/meta");
  },

  getPodActions: function(podID) {
    return API.get('pods/' + podID + '/actionlogs');
  },

  /**
   * @param {string} nodeID
   * @param {AbortController} [abortController]
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  getPodsByNodeID: function(nodeID, abortController) {
    return API.get('nodes/' + nodeID + '/pods', {
      signal: abortController?.signal ?? undefined,
    });
  },

  createPod: function(data) {
    const body = data;
    return API.post('pods', body);
  },

  /**
   * Pre-create many pods - prepares folders and generates IDs
   * @param {{
   *   nodeid: string,
   *   useruuid: string,
   *   teamonly: 0|1,
   *   pods: ({
   *     uuid: string,
   *     filename: string,
   *     relativePath: string,
   *   })[],
   * }} data
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  preCreateManyPods: function(data) {
    return API.post('pods/precreate-many', data);
  },

  /**
   *
   * @param {{
   *   nodeid: string,
   *   useruuid: string,
   *   labels: ({
   *     id: string,
   *     name: string,
   *   })[],
   *   pods: ({
   *     filename: string,
   *     uuid: string,
   *     filesize: number,
   *     note: string,
   *     badgestatus: number,
   *     teamonly: boolean,
   *     relativePath: string,
   *     lastModified: string,
   *   })[],
   * }} data
   * @returns {Promise<axios.AxiosResponse<any>>}
   */
  createManyPods: function(data) {
    return API.post('pods/create-many', data);
  },

  updatePod: function(data) {
    return API.patch('pods/' + data.id, data);
  },
  updatePodNote: function(data) {
    const body = {
      note: data.note
    };

    return API.patch('pods/' + data.podId + '/note', body);
  },

  updateBadgeStatus: function(data) {
    const body = {
      badgestatus: data.badgestatus
    };

    return API.patch('pods/' + data.id + '/status', body);
  },

  transcodeFile: function(data) {
    const body = {};
    return API.post('pods/' + data.id +"/transcode", body);
  },

  extractArchive: function(data) {
    const body = {
      conflictResolutions: data.conflictResolutions ?? undefined,
      password: data.password ?? undefined,
    };

    return API.post('pods/' + data.id + '/extract', body);
  },

  generateAudio: function(data) {
    const body = {};
    return API.post('pods/' + data.id +"/audio", body);
  },

  /**
   * @param {string} id
   * @param {boolean} permanently
   * @returns {Promise<string[]>}
   */
  deletePod: async function (id, permanently) {
    const body = {
      permanently,
    };

    return await API.post(`pods/${id}/delete`, body).then(r => r.data);
  },

  /**
   * @param {string[]} ids
   * @param {boolean} permanently
   * @returns {Promise<string[]>}
   */
  deleteManyPods: async function(ids, permanently) {
    const result = await executeChunkedRequests({
      items: ids,
      chunkSize: BackendLimits.MAX_PODS_PER_DELETE,
      requestFn: async (chunk) => {
        const response = await API.post('pods/bulk-delete', { pods: chunk, permanently });
        return response.data || response;
      },
      aggregateFn: (results) => results.flat(),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },

  createPodAsset: function(data) {
    const body = data;
    return API.post('pods/' + data.podid + '/asset', body);
  },

  updatePodAsset: function(data) {
    const body = data.payload;
    return API.patch('pods/' + data.podID + '/assets/' + data.assetID, body);
  },

  deletePodAsset: function(podID, assetID) {
    const body = {};
    return API.delete('pods/' + podID + '/assets/' + assetID, body);
  },

  issueRawAssetDownloadUrl: function(podID, assetID) {
    return API.post(`pods/${podID}/assets/${assetID}/download-url`);
  },

  setAssetLanguage: function(podID, assetID, languageID) {
    const body = {
      language: languageID,
    };

    return API.patch(`/pods/${podID}/assets/${assetID}/languages`, body);
  },

  createSubtitleAsset: function(data) {
    const body = data.payload;
    return API.post('pods/' + data.podID + '/subtitles', body);
  },

  updateSubtitleAsset: async function(data) {
    const body = data.payload;
    return API.patch('pods/' + data.podID + '/subtitles/' + data.assetID, body);
  },

  promoteAssetToPod: function(data) {
    const body = data.payload;
    return API.post('/pods/' + data.podID + '/assets/' + data.assetID + '/promote', body);
  },

  moveManyPods: async function(data) {
    const { pods, ...otherParams } = data;

    const result = await executeChunkedRequests({
      items: pods,
      chunkSize: BackendLimits.MAX_PODS_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.patch('pods/move', { pods: chunk, ...otherParams });
        return response.data || response;
      },
      aggregateFn: (results) => results.flat(),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return { data: result.data };
  },

  copyManyPods: async function(data) {
    const { pods, ...otherParams } = data;

    const result = await executeChunkedRequests({
      items: pods,
      chunkSize: BackendLimits.MAX_PODS_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.post('pods/copy', { pods: chunk, ...otherParams });
        return response.data || response;
      },
      aggregateFn: (results) => ({
        targetNodeId: results[0]?.targetNodeId,
        filesSize: results.reduce((sum, r) => sum + (r?.filesSize || 0), 0),
        unprocessablePods: results.flatMap(r => r?.unprocessablePods || []),
      }),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },

  copyManyPodsCrossTenant: async function(data) {
    const { pods, ...otherParams } = data;

    const result = await executeChunkedRequests({
      items: pods,
      chunkSize: BackendLimits.MAX_PODS_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.post('pods/copy-cross-tenant', { pods: chunk, ...otherParams });
        return response.data || response;
      },
      aggregateFn: (results) => ({
        targetNodeId: results[0]?.targetNodeId,
        filesSize: results.reduce((sum, r) => sum + (r?.filesSize || 0), 0),
        unprocessablePods: results.flatMap(r => r?.unprocessablePods || []),
      }),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return result.data;
  },

  convertSubtitle: function(data) {
    const body = data.payload;
    return API.post('/pods/' + data.podID + '/assets/' + data.assetID + '/convert', body);
  },

  addVersion: function(payload) {
    const body = {
      versionText: payload.versionText,
    };
    return API.patch(`pods/${payload.sourcePodId}/versions/${payload.targetPodId}`, body);
  },
  reorderVersions: function(payload) {
    const body = {
      podids: payload.podids,
    };
    return API.post(`pods/${payload.podid}/reorder`, body);
  },
  updateTeamOnly: function(data) {
    const body = {
      teamonly: data.teamonly,
    };

    return API.patch('pods/' + data.id + '/teamonly', body);
  },
  updateTeamOnlyPods: async function(data) {
    const teamonly = data.teamonly ? 1 : 0;

    const result = await executeChunkedRequests({
      items: data.pods,
      chunkSize: BackendLimits.MAX_PODS_PER_OPERATION,
      requestFn: async (chunk) => {
        const response = await API.patch('pods/bulk-teamonly', { pods: chunk, teamonly });
        return response.data || response;
      },
      aggregateFn: (results) => results.flat(),
      errorStrategy: { stopOnError: true },
    });

    if (!result.success && !result.partialSuccess) {
      throw result.errors[0]?.error;
    }
    return { data: result.data };
  },
  renameFile: function(data) {
    const body = {
      name: data.name,
    };

    return API.patch(`pods/${data.id}/rename`, body);
  },
  getAnalytics: function(data) {
    const parseJsonWithBigInts = (data, headers) => {
      const contentType = headers['content-type'] ?? headers['Content-Type'] ?? null;
      if (contentType && contentType.includes('application/json')) {
        return JSON.parse(data, (key, value) =>
          Number.isInteger(value) && !Number.isSafeInteger(value)
            ? BigInt(value)
            : value
        );
      }

      return data;
    };

    if (data.from && data.to) {
      return API.get(`pods/${data.id}/analytics?from=${data.from}&to=${data.to}`, {
        transformResponse: parseJsonWithBigInts,
      });
    }

    return API.get(`pods/${data.id}/analytics`, {
      transformResponse: parseJsonWithBigInts,
    });
  },
  convertImage: function (data) {
    const body = {
      format: data.format,
      quality: data.quality,
    };

    return API.post(`pods/${data.id}/convert-image`, body);
  },
  listArchiveContents: async function(id) {
    const res = await API.get(`pods/${id}/list-archive-contents`);

    if (Object.keys(res.data).length === 0) {
      // Operation queued for processing
      return null;
    }

    return res.data;
  }
};
